"use client";

import React, { useCallback, useState } from "react";
import { Building2, Loader2, LogOut, ShieldCheck, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/lib/hooks/use-translation";
import { cn } from "@/lib/utils";

export type LogoutConfirmSession = {
  accountLabel: string;
  organizationLabel: string;
  roleLabel: string;
};

type LogoutConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: LogoutConfirmSession;
  onConfirm: () => void | Promise<void>;
};

function SessionRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="truncate text-sm font-semibold text-foreground" title={value}>
          {value}
        </p>
      </div>
    </div>
  );
}

export function LogoutConfirmDialog({
  open,
  onOpenChange,
  session,
  onConfirm,
}: LogoutConfirmDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (isSigningOut && !next) {
        return;
      }
      onOpenChange(next);
      if (!next) {
        setIsSigningOut(false);
      }
    },
    [isSigningOut, onOpenChange],
  );

  const handleConfirm = useCallback(async () => {
    if (isSigningOut) {
      return;
    }
    setIsSigningOut(true);
    try {
      await onConfirm();
    } finally {
      setIsSigningOut(false);
    }
  }, [isSigningOut, onConfirm]);

  const effects = [
    t("auth.logoutConfirmEffectSession"),
    t("auth.logoutConfirmEffectWorkspace"),
    t("auth.logoutConfirmEffectDevice"),
  ];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={!isSigningOut}
        data-testid="logout-confirm-dialog"
        className="gap-0 overflow-hidden p-0 sm:max-w-md"
        onEscapeKeyDown={(event) => {
          if (isSigningOut) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          if (isSigningOut) {
            event.preventDefault();
          }
        }}
      >
        <div className="relative overflow-hidden border-b border-border bg-gradient-to-br from-destructive/[0.08] via-background to-background px-5 pb-4 pt-5 sm:px-6 sm:pt-6">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-10 -top-12 h-36 w-36 rounded-full bg-destructive/10 blur-2xl"
          />
          <DialogHeader className="relative gap-3 text-left">
            <div className="flex items-start gap-3.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-destructive/25 bg-destructive/10 text-destructive">
                <LogOut className="h-4 w-4" strokeWidth={2.25} />
              </span>
              <div className="min-w-0 space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {t("auth.platformTitle")}
                </p>
                <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
                  {t("auth.logoutConfirmTitle")}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  {t("auth.logoutConfirmDescription")}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="space-y-4 px-5 py-4 sm:px-6">
          <section
            aria-label={t("auth.logoutConfirmSessionLabel")}
            className="rounded-xl border border-border/80 bg-muted/35 p-3.5 shadow-sm"
          >
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t("auth.logoutConfirmSessionLabel")}
            </p>
            <div className="space-y-3">
              <SessionRow
                icon={UserRound}
                label={t("auth.logoutConfirmAccount")}
                value={session.accountLabel || t("auth.logoutConfirmUnknownAccount")}
              />
              <SessionRow
                icon={Building2}
                label={t("auth.logoutConfirmOrganization")}
                value={session.organizationLabel || t("auth.logoutConfirmUnknownOrganization")}
              />
              <SessionRow
                icon={ShieldCheck}
                label={t("auth.logoutConfirmRole")}
                value={session.roleLabel || t("auth.logoutConfirmUnknownRole")}
              />
            </div>
          </section>

          <ul className="space-y-2" aria-label={t("auth.logoutConfirmEffectsLabel")}>
            {effects.map((effect) => (
              <li key={effect} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                <span
                  aria-hidden
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive/70"
                />
                <span className="leading-snug">{effect}</span>
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter className="gap-2 border-t border-border bg-muted/20 px-5 py-4 sm:flex-row sm:justify-end sm:space-x-0 sm:px-6">
          <Button
            type="button"
            variant="outline"
            className="h-10 rounded-lg sm:min-w-[8.5rem]"
            disabled={isSigningOut}
            onClick={() => handleOpenChange(false)}
          >
            {t("auth.logoutConfirmCancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className={cn("h-10 rounded-lg font-semibold sm:min-w-[8.5rem]")}
            disabled={isSigningOut}
            data-testid="logout-confirm-submit"
            onClick={() => {
              void handleConfirm();
            }}
          >
            {isSigningOut ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("auth.loggingOut")}
              </>
            ) : (
              <>
                <LogOut className="h-4 w-4" />
                {t("auth.logoutConfirmConfirm")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
